/**
 * @fileoverview Base API client implementation for the unified API layer
 * Provides common HTTP operations, error handling, retry logic, and interceptors
 */

import type {
  ApiRequestConfig,
  ApiResponse,
  ApiClientConfig,
  RequestInterceptor,
  ResponseInterceptor,
  StandardApiResponse,
  RequestOptions
} from './types';
import { ApiError as ApiErrorClass } from './errors';

export abstract class BaseApiClient {
  protected baseURL: string;
  protected config: ApiClientConfig;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  
  constructor(config: { baseURL: string } & ApiClientConfig) {
    this.baseURL = config.baseURL.replace(/\/$/, ''); // Remove trailing slash
    
    // Set default configuration
    this.config = {
      timeout: config.timeout ?? 10000,
      retries: config.retries ?? 3,
      retryDelay: config.retryDelay ?? 1000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers
      },
      requestInterceptors: config.requestInterceptors ?? [],
      responseInterceptors: config.responseInterceptors ?? []
    };

    // Add provided interceptors
    this.requestInterceptors = [...this.config.requestInterceptors!];
    this.responseInterceptors = [...this.config.responseInterceptors!];
  }

  // Core HTTP methods
  protected async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>({
      method: 'GET',
      ...options
    }, endpoint);
  }

  protected async post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>({
      method: 'POST',
      data,
      ...options
    }, endpoint);
  }

  protected async put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>({
      method: 'PUT',
      data,
      ...options
    }, endpoint);
  }

  protected async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>({
      method: 'DELETE',
      ...options
    }, endpoint);
  }

  protected async patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>({
      method: 'PATCH',
      data,
      ...options
    }, endpoint);
  }

  // Core request method with interceptors and retry logic
  private async request<T>(config: ApiRequestConfig, endpoint: string): Promise<T> {
    const url = this.buildUrl(endpoint);
    
    // Force debug logging for URL construction
    
    let requestConfig: ApiRequestConfig = {
      ...config,
      headers: {
        ...this.config.headers,
        ...config.headers
      },
      timeout: config.timeout ?? this.config.timeout
    };

    // Apply request interceptors
    for (const interceptor of this.requestInterceptors) {
      if (interceptor.onRequest) {
        try {
          requestConfig = await interceptor.onRequest(requestConfig);
        } catch (error) {
          if (interceptor.onRequestError) {
            throw interceptor.onRequestError(error);
          }
          throw error;
        }
      }
    }

    // Execute request with retry logic
    return this.executeWithRetry<T>(url, requestConfig);
  }

  private async executeWithRetry<T>(url: string, config: ApiRequestConfig): Promise<T> {
    const maxRetries = config.retries ?? this.config.retries ?? 3;
    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await this.executeRequest<T>(url, config);
        
        // Apply response interceptors
        let processedResponse = response;
        for (const interceptor of this.responseInterceptors) {
          if (interceptor.onResponse) {
            processedResponse = await interceptor.onResponse(processedResponse);
          }
        }

        return this.transformResponse<T>(processedResponse);
      } catch (error: unknown) {
        lastError = error;

        // Apply error interceptors - only if error is ApiError type
        if (error instanceof ApiErrorClass) {
          for (const interceptor of this.responseInterceptors) {
            if (interceptor.onResponseError) {
              try {
                // Type assertion needed as interceptor may transform the error
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return interceptor.onResponseError(error as any) as T;
              } catch (interceptorError) {
                lastError = interceptorError;
              }
            }
          }
        }

        // Don't retry on client errors (4xx) or if it's the last attempt
        if (this.isClientError(error) || attempt === maxRetries) {
          throw lastError;
        }

        // Wait before retry
        if (attempt < maxRetries) {
          const delay = config.retryDelay ?? this.config.retryDelay ?? 1000;
          await this.delay(delay * Math.pow(2, attempt)); // Exponential backoff
        }
      }
    }

    throw lastError;
  }

  private async executeRequest<T>(url: string, config: ApiRequestConfig): Promise<ApiResponse<T>> {
    const controller = config.abortSignal ? undefined : new AbortController();
    const signal = config.abortSignal ?? controller?.signal;

    // Set up timeout
    const timeoutId = config.timeout ? setTimeout(() => {
      controller?.abort();
    }, config.timeout) : null;

    try {
      const fetchOptions: RequestInit = {
        method: config.method ?? 'GET',
        headers: config.headers,
        signal,
        body: config.data ? JSON.stringify(config.data) : undefined
      };

      // Add query parameters
      const requestUrl = this.buildUrlWithParams(url, config.params);
      
      const response = await fetch(requestUrl, fetchOptions);

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Handle HTTP errors
      if (!response.ok) {
        throw await this.createErrorFromResponse(response, config);
      }

      // Handle responses with no content
      let data: unknown = null;
      if (response.status !== 204 && response.headers.get('content-length') !== '0') {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          data = await response.text();
        }
      }

      return {
        data: data as T,
        status: response.status,
        statusText: response.statusText,
        headers: this.extractHeaders(response.headers),
        config
      };
    } catch (error) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Handle network and other errors
      if (error instanceof ApiErrorClass) {
        throw error;
      }

      throw this.createErrorFromNetworkError(error, url, config);
    }
  }

  private async createErrorFromResponse(response: Response, config: ApiRequestConfig): Promise<ApiErrorClass> {
    let errorData: Record<string, unknown> = {};
    
    try {
      errorData = await response.json();
    } catch {
      // Response is not JSON
    }

    const nestedError = errorData.error;
    const nestedErrorMessage = (
      typeof nestedError === 'object' &&
      nestedError !== null &&
      'message' in nestedError &&
      typeof (nestedError as Record<string, unknown>).message === 'string'
    )
      ? ((nestedError as Record<string, unknown>).message as string)
      : undefined;

    const errorMessage =
      (typeof errorData.message === 'string' ? errorData.message : undefined) ||
      (typeof errorData.error === 'string' ? errorData.error : undefined) ||
      nestedErrorMessage ||
      response.statusText ||
      'Request failed';

    return new ApiErrorClass(
      errorMessage,
      response.status,
      (errorData.code as string) || this.getErrorCodeFromStatus(response.status),
      `${config.method} ${response.url}`,
      errorData.details
    );
  }

  private createErrorFromNetworkError(error: unknown, url: string, config: ApiRequestConfig): ApiErrorClass {
    if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
      return new ApiErrorClass(
        'Request timeout',
        0,
        'TIMEOUT',
        `${config.method} ${url}`
      );
    }

    const errorMessage = error instanceof Error ? error.message : 'Network error occurred';
    
    return new ApiErrorClass(
      errorMessage,
      0,
      'NETWORK_ERROR',
      `${config.method} ${url}`
    );
  }

  private getErrorCodeFromStatus(status: number): string {
    switch (status) {
      case 400: return 'BAD_REQUEST';
      case 401: return 'UNAUTHORIZED';
      case 403: return 'FORBIDDEN';
      case 404: return 'NOT_FOUND';
      case 409: return 'CONFLICT';
      case 422: return 'VALIDATION_ERROR';
      case 429: return 'RATE_LIMITED';
      case 500: return 'INTERNAL_SERVER_ERROR';
      case 502: return 'BAD_GATEWAY';
      case 503: return 'SERVICE_UNAVAILABLE';
      case 504: return 'GATEWAY_TIMEOUT';
      default: return 'UNKNOWN_ERROR';
    }
  }

  private transformResponse<T>(response: ApiResponse<any>): T {
    // Handle null/undefined response data (e.g., 204 No Content)
    if (response.data === null || response.data === undefined) {
      return response.data as T;
    }

    // Check if response follows standard API format
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const standardResponse = response.data as StandardApiResponse<T>;
      return standardResponse.data;
    }

    // Return raw data if not in standard format
    return response.data;
  }

  private buildUrl(endpoint: string): string {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${this.baseURL}/${cleanEndpoint}`;
  }

  private buildUrlWithParams(url: string, params?: Record<string, any>): string {
    if (!params || Object.keys(params).length === 0) {
      return url;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    return `${url}?${searchParams.toString()}`;
  }

  private extractHeaders(headers: Headers): Record<string, string> {
    const headerObj: Record<string, string> = {};
    headers.forEach((value, key) => {
      headerObj[key] = value;
    });
    return headerObj;
  }

  private isClientError(error: unknown): boolean {
    return error instanceof ApiErrorClass && error.statusCode >= 400 && error.statusCode < 500;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Interceptor management
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  removeRequestInterceptor(interceptor: RequestInterceptor): void {
    const index = this.requestInterceptors.indexOf(interceptor);
    if (index > -1) {
      this.requestInterceptors.splice(index, 1);
    }
  }

  removeResponseInterceptor(interceptor: ResponseInterceptor): void {
    const index = this.responseInterceptors.indexOf(interceptor);
    if (index > -1) {
      this.responseInterceptors.splice(index, 1);
    }
  }
}
